import { useState, useCallback } from "react";
import {
  InvoiceDataEntity,
  CreateZohoInvoiceUseCase,
  UpdateZohoInvoiceUseCase,
  RejectInvoiceUseCase,
  MarkInvoiceAsDuplicateUseCase,
  DeletePostedInvoiceUseCase,
  UpdateInvoiceStatusUseCase,
  UpdateInvoiceJsonUseCase,
} from "../../domain";
import { InvoiceRepositoryImpl } from "../../data/repositories/InvoiceRepositoryImpl";
import type { ApprovalAuditPayload } from "../../../../shared/utils/reviewWorkflow";
import {
  getFailedCreateInBackendMessage,
  getFailedUpdateInBackendMessage,
  getStoredAccountingBackend,
} from "../../../../config/accounting";

// Create repository instance (singleton)
const invoiceRepository = new InvoiceRepositoryImpl();

// Create use cases
const createZohoInvoiceUseCase = new CreateZohoInvoiceUseCase(
  invoiceRepository,
);
const updateZohoInvoiceUseCase = new UpdateZohoInvoiceUseCase(
  invoiceRepository,
);
const rejectInvoiceUseCase = new RejectInvoiceUseCase(invoiceRepository);
const markInvoiceAsDuplicateUseCase = new MarkInvoiceAsDuplicateUseCase(
  invoiceRepository,
);
const deletePostedInvoiceUseCase = new DeletePostedInvoiceUseCase(
  invoiceRepository,
);
const updateInvoiceStatusUseCase = new UpdateInvoiceStatusUseCase(
  invoiceRepository,
);
const updateInvoiceJsonUseCase = new UpdateInvoiceJsonUseCase(
  invoiceRepository,
);

export interface UseInvoiceOperationsReturn {
  loading: boolean;
  error: string | null;

  createZohoInvoice: (
    invoiceId: number,
    data: InvoiceDataEntity,
    pdfUrl: string,
    approvalAudit?: ApprovalAuditPayload,
  ) => Promise<boolean>;
  updateZohoInvoice: (
    invoiceId: number,
    data: InvoiceDataEntity,
    approvalAudit?: ApprovalAuditPayload,
  ) => Promise<boolean>;
  updateInvoiceJson: (
    invoiceId: number,
    data: InvoiceDataEntity,
  ) => Promise<boolean>;
  updateInvoiceStatus: (invoiceId: number, status: number) => Promise<boolean>;
  rejectInvoice: (invoiceId: number) => Promise<boolean>;
  markAsDuplicate: (invoiceId: number) => Promise<boolean>;
  deletePostedInvoice: (invoiceId: number) => Promise<boolean>;

  clearError: () => void;
}

export const useInvoiceOperations = (): UseInvoiceOperationsReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const accountingBackend = getStoredAccountingBackend();

  /**
   * Create invoice in Zoho Books
   */
  const createZohoInvoice = useCallback(
    async (
      invoiceId: number,
      data: InvoiceDataEntity,
      pdfUrl: string,
      approvalAudit?: ApprovalAuditPayload,
    ): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await createZohoInvoiceUseCase.execute(
          invoiceId,
          data,
          pdfUrl,
          approvalAudit,
        );

        if (!result.success) {
          throw new Error(result.error || getFailedCreateInBackendMessage("invoice", accountingBackend));
        }

        return result.success;
      } catch (error: any) {
        const errorMessage =
          error.message || getFailedCreateInBackendMessage("invoice", accountingBackend);
        setError(errorMessage);
        console.error("Failed to create Zoho invoice:", error);
        // Re-throw the error so it can be caught by the component immediately
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [accountingBackend],
  );

  /**
   * Update invoice in accounting backend
   */
  const updateZohoInvoice = useCallback(
    async (
      invoiceId: number,
      data: InvoiceDataEntity,
      approvalAudit?: ApprovalAuditPayload,
    ): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await updateZohoInvoiceUseCase.execute(
          invoiceId,
          data,
          approvalAudit,
        );

        if (!result.success) {
          throw new Error(result.error || getFailedUpdateInBackendMessage("invoice", accountingBackend));
        }

        return result.success;
      } catch (error: any) {
        const errorMessage =
          error.message || getFailedUpdateInBackendMessage("invoice", accountingBackend);
        setError(errorMessage);
        console.error("Failed to update Zoho invoice:", error);
        // Re-throw the error so it can be caught by the component immediately
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [accountingBackend],
  );

  /**
   * Update invoice JSON data (for auto-save and manual edits)
   */
  const updateInvoiceJson = useCallback(
    async (invoiceId: number, data: InvoiceDataEntity): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await updateInvoiceJsonUseCase.execute(invoiceId, data);

        if (!result.success) {
          throw new Error(result.error || "Failed to update invoice JSON");
        }

        return true;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to update invoice JSON";
        setError(errorMessage);
        console.error("Failed to update invoice JSON:", error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  /**
   * Update invoice status
   */
  const updateInvoiceStatus = useCallback(
    async (invoiceId: number, status: number): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await updateInvoiceStatusUseCase.execute(
          invoiceId,
          status,
        );

        if (!result.success) {
          throw new Error(result.error || "Failed to update invoice status");
        }

        return true;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to update invoice status";
        setError(errorMessage);
        console.error("Failed to update invoice status:", error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  /**
   * Reject an invoice
   */
  const rejectInvoice = useCallback(
    async (invoiceId: number): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await rejectInvoiceUseCase.execute(invoiceId);

        if (!result.success) {
          throw new Error(result.error || "Failed to reject invoice");
        }

        return true;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to reject invoice";
        setError(errorMessage);
        console.error("Failed to reject invoice:", error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  /**
   * Mark invoice as duplicate
   */
  const markAsDuplicate = useCallback(
    async (invoiceId: number): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await markInvoiceAsDuplicateUseCase.execute(invoiceId);

        if (!result.success) {
          throw new Error(
            result.error || "Failed to mark invoice as duplicate",
          );
        }

        return true;
      } catch (error: any) {
        const errorMessage =
          error.message || "Failed to mark invoice as duplicate";
        setError(errorMessage);
        console.error("Failed to mark invoice as duplicate:", error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  /**
   * Delete posted invoice (from Zoho and soft-delete locally)
   */
  const deletePostedInvoice = useCallback(
    async (invoiceId: number): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const result = await deletePostedInvoiceUseCase.execute(invoiceId);

        if (!result.success) {
          throw new Error(result.error || "Failed to delete invoice");
        }

        return true;
      } catch (error: any) {
        const errorMessage = error.message || "Failed to delete invoice";
        setError(errorMessage);
        console.error("Failed to delete invoice:", error);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  /**
   * Clear error
   */
  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    createZohoInvoice,
    updateZohoInvoice,
    updateInvoiceJson,
    updateInvoiceStatus,
    rejectInvoice,
    markAsDuplicate,
    deletePostedInvoice,
    clearError,
  };
};
